import { Box, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { CantonSelect, MonthPicker, PostalAndCity, StreetAndHouseNumber } from "components";
import { getRequiredFieldStyles } from "pages/clients/view/components/required-field-styles";
import { formClasses } from "styles";
import { BankCreditRequestForm, SwissCanton } from "types";

import SectionTitle from "../../../components/section-title";

interface AddressSectionProps {
  form: UseFormReturnType<BankCreditRequestForm>;
  sectionTitle: string;
  fieldNames: {
    postal: keyof BankCreditRequestForm;
    city: keyof BankCreditRequestForm;
    street: keyof BankCreditRequestForm;
    houseNumber: keyof BankCreditRequestForm;
    canton?: "canton" | "previousCanton" | "mortgageCanton";
    livingFrom?: keyof BankCreditRequestForm;
  };
  cantonList?: SwissCanton[];
  readOnly?: boolean;
  isBankCredit?: boolean;
}

export const AddressSection = ({
  form,
  sectionTitle,
  fieldNames,
  cantonList,
  readOnly = false,
  isBankCredit = true,
}: AddressSectionProps) => {
  const { t } = useTranslation();

  const getRequiredMonthPickerStyles = (fieldName: keyof BankCreditRequestForm) => {
    const value = form.getValues()?.[fieldName];
    const isEmpty = !value;

    return getRequiredFieldStyles({
      readOnly,
      isEmpty,
    });
  };

  return (
    <Box className={formClasses.sectionBorder}>
      <SectionTitle label={sectionTitle} isBankCredit={isBankCredit} />

      <SimpleGrid cols={4}>
        <PostalAndCity
          form={form}
          postalFieldName={fieldNames.postal}
          cityFieldName={fieldNames.city}
          cantonFieldName={fieldNames.canton}
          cantonOptions={cantonList}
          disabled={readOnly}
        />
        <StreetAndHouseNumber
          form={form}
          streetFieldName={fieldNames.street}
          houseNumberFieldName={fieldNames.houseNumber}
          disabled={readOnly}
        />
        {fieldNames.canton && cantonList && (
          <CantonSelect
            fieldName={fieldNames.canton}
            form={form}
            list={cantonList}
            disabled={readOnly}
          />
        )}
        {fieldNames.livingFrom && (
          <MonthPicker
            label={t("user.livingFrom.label")}
            key={form.key(fieldNames.livingFrom)}
            {...form.getInputProps(fieldNames.livingFrom)}
            maxDate={new Date()}
            required={true}
            styles={getRequiredMonthPickerStyles(fieldNames.livingFrom)}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>
    </Box>
  );
};
